import React, { useEffect, useState } from 'react';
import { Spin, Alert, Empty, Row, Col, Divider, message, Card, Space, Button } from 'antd';
import {
  UserOutlined,
  IdcardOutlined,
  UnderlineOutlined,
  MailOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { fetchUserProfile, MeResponse } from '@/services/identity/me.service';
import { PersonalAccessTokensContainer } from '../components/PersonalAccessTokensContainer';

export const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<MeResponse | null>(null);

  const handleCopyId = async () => {
    if (profile?.id) {
      try {
        await navigator.clipboard.writeText(profile.id);
        message.success('ID copied to clipboard');
      } catch (error) {
        message.error('Failed to copy ID to clipboard');
      }
    }
  };

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchUserProfile();
        setProfile(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load profile';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
        }}
      >
        <Spin size='large' />
      </div>
    );
  }

  if (error) {
    return <Alert message={t('common.error')} description={error} type='error' showIcon />;
  }

  if (!profile) {
    return <Empty description={t('common.userProfile')} />;
  }

  return (
    <div style={{ paddingLeft: '48px', paddingRight: '48px', paddingTop: '24px' }}>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={6}>
          <Card bordered={false}>
            <Space direction='vertical' style={{ width: '100%', textAlign: 'center' }}>
              <UserOutlined
                style={{
                  fontSize: '48px',
                  color: '#1890ff',
                }}
              />
              <h2 style={{ margin: 0 }}>{profile.displayName || profile.username || '-'}</h2>
            </Space>
            <Divider style={{ margin: '24px 0' }} />
            <Space direction='vertical' style={{ width: '100%' }} size='middle'>
              <Space size={8} wrap>
                <MailOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                <span style={{ wordBreak: 'break-all', fontSize: '14px' }}>
                  {profile.email || '-'}
                </span>
              </Space>
              <Space size={8} wrap>
                <IdcardOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                <span style={{ wordBreak: 'break-all', fontSize: '14px' }}>
                  {profile.id || '-'}
                </span>
                <Button
                  type='text'
                  size='small'
                  icon={<CopyOutlined />}
                  onClick={handleCopyId}
                  title='Copy ID'
                  style={{ padding: '0 4px' }}
                />
              </Space>
              <Space size={8} wrap>
                <UnderlineOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                <span style={{ fontSize: '14px' }}>{profile.username || '-'}</span>
              </Space>
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={18}>
          <Card bordered={false}>
            <PersonalAccessTokensContainer />
          </Card>
        </Col>
      </Row>
    </div>
  );
};
